/**
 * Componente de estadísticas de mantenimiento
 */

import { Card } from '@/components/ui/card';
import {
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  Wrench,
  CalendarClock,
} from 'lucide-react';
import { useMaintenanceStats } from '@/hooks/usePlotterMaintenance';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { TASK_TYPE_CONFIG } from '@/types/plotterMaintenance';

interface MaintenanceStatsProps {
  machineId?: string | null;
}

export function MaintenanceStats({ machineId }: MaintenanceStatsProps) {
  const stats = useMaintenanceStats(machineId);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
      {/* Total */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Total</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <Wrench className="w-8 h-8 text-primary/30" />
        </div>
      </Card>

      {/* Programadas */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Programadas</p>
            <p className="text-2xl font-bold text-blue-500">{stats.scheduled}</p>
          </div>
          <Calendar className="w-8 h-8 text-blue-500/30" />
        </div>
      </Card>

      {/* En Progreso */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">En Progreso</p>
            <p className="text-2xl font-bold text-yellow-500">{stats.inProgress}</p>
          </div>
          <Clock className="w-8 h-8 text-yellow-500/30" />
        </div>
      </Card>

      {/* Completadas */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Completadas</p>
            <p className="text-2xl font-bold text-green-500">{stats.completed}</p>
          </div>
          <CheckCircle className="w-8 h-8 text-green-500/30" />
        </div>
      </Card>

      {/* Vencidas */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Vencidas</p>
            <p className="text-2xl font-bold text-red-500">{stats.overdue}</p>
          </div>
          <AlertTriangle className="w-8 h-8 text-red-500/30" />
        </div>
      </Card>

      {/* Próxima tarea */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Próxima</p>
            {stats.nextTask ? (
              <p className="text-sm font-medium truncate" title={stats.nextTask.title}>
                {format(new Date(stats.nextTask.scheduled_date), 'dd MMM', { locale: es })}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">-</p>
            )}
          </div>
          <CalendarClock className="w-8 h-8 text-purple-500/30" />
        </div>
      </Card>
    </div>
  );
}
